import React, { useState } from 'react';
import { Package, Truck, X, Plus, Search } from 'lucide-react';
import type { WorkOrderDetailViewModel } from '../types/workOrderDetail.types';
import { workOrderDetailService } from '../services/workOrderDetail.service';
import { toast } from 'sonner';

interface ResourceAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: WorkOrderDetailViewModel;
  type: 'material' | 'equipment';
  onSuccess: () => void;
}

export function ResourceAssignmentModal({ 
  isOpen, 
  onClose, 
  data, 
  type, 
  onSuccess 
}: ResourceAssignmentModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [unit, setUnit] = useState('lot');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || quantity <= 0) {
      toast.error('Vui lòng nhập đầy đủ thông tin');
      return;
    }

    try {
      setIsSubmitting(true);
      await workOrderDetailService.assignResources(
        data.id,
        data.projectId,
        data.companyId,
        {
          type,
          items: [
            type === 'material' 
              ? { name, quantity, unit } 
              : { name, hours: quantity }
          ]
        }
      );
      
      toast.success(`Đã gán ${type === 'material' ? 'vật tư' : 'thiết bị'} thành công`);
      onSuccess();
      onClose();
      // Reset form
      setName('');
      setQuantity(0);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-md w-full max-w-md overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {type === 'material' ? (
              <Package className="h-5 w-5 text-emerald-600" />
            ) : (
              <Truck className="h-5 w-5 text-blue-600" />
            )}
            <h3 className="sm:text-sm text-xs font-normal text-slate-900">
              Gán {type === 'material' ? 'vật tư thi công' : 'thiết bị thi công'}
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="space-y-2">
            <label className="sm:text-sm text-xs text-slate-500">
              Tên {type === 'material' ? 'vật tư' : 'thiết bị'}
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={type === 'material' ? 'Ví dụ: Xi măng PCB40...' : 'Ví dụ: Máy xúc Kobelco...'}
                className="w-full h-10 pl-3 pr-10 rounded-md border border-slate-200 sm:text-sm text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-slate-50/50"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
              <Search className="absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="sm:text-sm text-xs text-slate-500">
                {type === 'material' ? 'Số lượng kế hoạch' : 'Số giờ kế hoạch'}
              </label>
              <input
                type="number"
                min="0.1"
                step="0.1"
                className="w-full h-10 px-3 rounded-md border border-slate-200 sm:text-sm text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-slate-50/50"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
              />
            </div>
            {type === 'material' && (
              <div className="space-y-2">
                <label className="sm:text-sm text-xs text-slate-500">Đơn vị</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-slate-200 sm:text-sm text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-slate-50/50"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                >
                  <option value="lot">Lô / Bộ</option>
                  <option value="kg">Kg</option>
                  <option value="ton">Tấn</option>
                  <option value="m2">m²</option>
                  <option value="m3">m³</option>
                  <option value="pcs">Cái / Chiếc</option>
                  <option value="bag">Bao</option>
                </select>
              </div>
            )}
          </div>

          <div className="rounded-md bg-blue-50 p-3 flex gap-2">
            <div className="shrink-0">
              <i className="ri-information-line text-blue-600" />
            </div>
            <p className="text-[11px] leading-relaxed text-blue-600">
              Việc gán nguồn lực tại đây chỉ mang tính chất lập kế hoạch. Quy trình xuất kho thực tế sẽ được thực hiện tại module Kho vật tư sau khi Work Order được phê duyệt.
            </p>
          </div>

          <div className="pt-4 flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="h-9 px-4 rounded-md border border-slate-200 bg-white sm:text-sm text-xs text-slate-600 hover:bg-slate-50"
              disabled={isSubmitting}
            >
              Hủy
            </button>
            <button
              type="submit"
              className="h-9 px-4 rounded-md bg-emerald-700 sm:text-sm text-xs text-white hover:bg-emerald-800 flex items-center gap-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Xác nhận gán
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
